import { useStore } from 'effector-react';
import { $catalogServicesStore } from 'store/catalog';
import { $dependentsStore } from 'store/dependents';
import { $legalEntitiesStore } from 'store/entities';
import { fetchEntityProfileFX } from 'store/entities/effects';
import { $mainStore } from 'store/main';
import { ProfileTypeEnum } from 'store/main/interface';
import { $userStore } from 'store/user';
import { fetchUserProfileFX } from 'store/user/effects';
import { LOCAL_STORAGE } from '../constants/app';
import { fetchPartnerOrganizationProfileFX } from '../store/partnerOrganizations/effects';
import { $partnerOrganizationStore } from '../store/partnerOrganizations';

type userProfile = () => {
  data: any;
  type: ProfileTypeEnum;
  isDependent: boolean;
  isOnBehalf: boolean;
  isLegalEntity: boolean;
  isPerson: boolean;
  isDependentOrOnBehalf: boolean;
  isPartnerOrganization: boolean;
  isLoading: boolean;
};

export const useProfile: userProfile = () => {
  const user = useStore($userStore);
  const { profile_type: storedProfileType } = useStore($mainStore);
  const { required_entities } = useStore($catalogServicesStore);
  const { single: dependentProfile } = useStore($dependentsStore);
  const { profile: entityProfile } = useStore($legalEntitiesStore);
  const { profile: partnerOrganizationProfile } = useStore($partnerOrganizationStore);
  const userProfileLoading = useStore(fetchUserProfileFX.pending);
  const entityProfileLoading = useStore(fetchEntityProfileFX.pending);
  const partnerOrganizationProfileLoading = useStore(fetchPartnerOrganizationProfileFX.pending);
  const selectedUUID = user?.accounts?.selected_uuid;
  const selectedProfileType =
    (localStorage.getItem(LOCAL_STORAGE.PROFILE_TYPE) as ProfileTypeEnum | null) ??
    storedProfileType;

  const getProfile = () => {
    const base = {
      isDependent: false,
      isOnBehalf: false,
      isLegalEntity: false,
      isPerson: false,
      isDependentOrOnBehalf: false,
      isPartnerOrganization: false,
    };

    // if legal entity selected
    if (entityProfile && selectedUUID) {
      const entityProfileUuid = (entityProfile as any)?.uuid;

      if (selectedUUID === entityProfile.legal_entity_uuid || selectedUUID === entityProfileUuid) {
        return dependentProfile
          ? {
              ...base,
              data: { ...dependentProfile, legal_entity_uuid: entityProfile.legal_entity_uuid },
              type: ProfileTypeEnum.DEPENDENT,
              isDependent: true,
              isDependentOrOnBehalf: true,
            }
          : {
              ...base,
              data: entityProfile,
              type: ProfileTypeEnum.LEGAL_ENTITY,
              isLegalEntity: true,
            };
      }
    }

    // if entity selected from catalog
    if (required_entities?.legal_entity?.uuid) {
      return {
        ...base,
        data: required_entities.legal_entity,
        type: ProfileTypeEnum.ON_BEHALF,
        isOnBehalf: true,
        isDependentOrOnBehalf: true,
      };
    }

    // if partner organization selected
    if (selectedUUID && selectedProfileType === ProfileTypeEnum.PARTNER_ORGANIZATION) {
      const selectedPartnerOrganizationProfile =
        partnerOrganizationProfile?.organization_uuid === selectedUUID
          ? partnerOrganizationProfile
          : null;

      return {
        ...base,
        data: selectedPartnerOrganizationProfile ?? {
          ...user,
          organization_uuid: selectedUUID,
        },
        type: ProfileTypeEnum.PARTNER_ORGANIZATION,
        isPartnerOrganization: true,
      };
    }

    // if person selected
    return {
      ...base,
      data: user,
      type: ProfileTypeEnum.PERSON,
      isPerson: true,
    };
  };

  const { data, ...rest } = getProfile();

  return {
    data: (data || {}) as any,
    isLoading: userProfileLoading || entityProfileLoading || partnerOrganizationProfileLoading,
    ...rest,
  };
};
